// Analytics: consent and region, the queue and its flush, the sinks, the page
// views and time per view, the error listeners, the tile byte observer and the
// per-visit caps. The call sites reach it only through src/visits/bus.ts.

import type * as THREE from 'three';

import type { AnalyticsSite, RelayRegion } from '../../shared/visitWire.ts';
import { isMode, onModeChange } from '../appMode.ts';
import { FRAME_BUCKETS, frameBucketFloorMs, profiler } from '../perf.ts';
import { devFlag, urlFlag, urlParam, TEST_SWITCHES } from '../urlFlags.ts';
import { clearAnalytics, type Analytics } from './bus.ts';
import { createConsentBar, createControlsRow } from './consentUi.ts';
import { ROUTES, type BandName, type EventName, type EventParams, type ParamValue, type Sink } from './events.ts';

export type Region = RelayRegion;
export type Consent = 'granted' | 'denied' | 'pending';

export interface SentEvent {
  readonly name: EventName;
  readonly params: Readonly<Record<string, ParamValue>>;
  readonly atMs: number;
}

export interface AnalyticsSink {
  send(events: readonly SentEvent[], opts: { beacon: boolean }): void;
  /** Consent was withdrawn: stop anything the sink keeps running. */
  stop?(): void;
}

/** The Overwatch relay: where the visitor is, and the sink its events go to. */
export interface AnalyticsRelay {
  /** Null when the relay cannot say, which is read as `eu`. */
  region(): Promise<Region | null>;
  readonly sink: AnalyticsSink;
}

export interface AnalyticsConfig {
  site: AnalyticsSite;
  /** GA4 web stream id. Empty or absent: no GA4. */
  measurementId?: string | undefined;
  /** The short git hash every event carries. */
  build?: string | undefined;
  relay?: AnalyticsRelay | null;
  renderer?: THREE.WebGLRenderer;
  /** Where the DEV recorder is published, as `scene.userData['simAnalytics']`. */
  scene?: THREE.Scene;
}

export interface AnalyticsHandle extends Analytics {
  dispose(): void;
}

/** One `track` call as the recorder saw it, before consent, caps or dedupe. */
export interface RecordedCall {
  readonly name: EventName;
  readonly frame: number;
  readonly atMs: number;
}

const QUEUE_MAX = 200;
const FLUSH_MS = 30_000;
const DEDUPE_MS = 2_000;
const WARP_SETTLE_MS = 3_000;
const PERF_VIEW_MIN_MS = 20_000;
const HIDDEN_END_MS = 30 * 60_000;
const REGION_WAIT_MS = 5_000;
const CAPS: Record<Sink, number> = { ga: 150, ow: 20 };
const EXCEPTION_CAP = 10;
const PERF_CAP = 6;
const CONSENT_KEY = 'fxc.analytics.consent';
// The same cookie the 4xciv.com site reads and writes.
const CONSENT_COOKIE = 'fxc_analytics_consent';
const CONSENT_MAX_AGE_S = 365 * 24 * 3600;
const SHARED_DOMAIN = '4xciv.com';
const BAND_ORDER: readonly BandName[] = ['SYSTEM', 'ORBIT', 'REGIONAL', 'LOCAL', 'CLAIM', 'PLOT', 'SURFACE'];

/**
 * `?analytics=record` swaps both sinks for an in-memory recorder; `?analytics=log` prints each event;
 * `?analytics=relay` records GA4 but runs the real relay sink, for `analytics-check.mjs --relay`.
 */
const devSinks = devFlag(() => {
  if (!TEST_SWITCHES) return false;
  const v = urlParam('analytics');
  return v === 'record' || v === 'log' || v === 'relay' ? v : false;
});
/** `?analyticsrelayearly=1`: each Overwatch event handed to the relay as it is tracked, `analytics-check.mjs --relay --inject relay-before-consent`. */
const relayEarly = devFlag(() => TEST_SWITCHES && urlFlag('analyticsrelayearly'));
/** `?analyticsdrop=<event>`: that event's call site swallowed, `analytics-check.mjs --inject drop=<event>`. */
const dropEvent = devFlag(() => TEST_SWITCHES && urlParam('analyticsdrop'));
/** `?analyticsregion=eu|row`: the region the relay would have answered. */
const regionOverride = devFlag(() => {
  if (!TEST_SWITCHES) return false;
  const v = urlParam('analyticsregion');
  return v === 'eu' || v === 'row' ? v : false;
});
/** `?analyticsconsentbypass=1`: the queue ignoring consent, `analytics-check.mjs --consent --inject consent-bypass`. */
const consentBypass = devFlag(() => TEST_SWITCHES && urlFlag('analyticsconsentbypass'));
/** `?analyticscookiehost=1`: the choice kept in a host-only cookie, `analytics-check.mjs --consent --inject host-cookie`. */
const hostOnlyCookie = devFlag(() => TEST_SWITCHES && urlFlag('analyticscookiehost'));
/** `?analyticsscalelast=1`: `render_scale_min` reads the scale at the view's close, `analytics-check.mjs --frames --inject scale-last`. */
const scaleAtClose = devFlag(() => TEST_SWITCHES && urlFlag('analyticsscalelast'));

const OFF: AnalyticsHandle = { track: () => {}, menuTab: () => {}, renderScale: () => {}, dispose: () => {} };

/** Query strings, ids and long numbers out, 100 characters at most. */
export function scrubDetail(s: string): string {
  return s
    .replace(/https?:\/\/[^\s)'"]+/g, (u) => {
      try { const x = new URL(u); return x.origin + x.pathname; } catch { return 'url'; }
    })
    .replace(/\?[^\s)'"]*/g, '')
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '#')
    .replace(/\b[0-9a-f]{16,}\b/gi, '#')
    .replace(/\d{7,}/g, '#')
    .slice(0, 100);
}

function referrerOf(raw: string): string {
  if (raw === '') return '';
  try { const u = new URL(raw); return (u.origin + u.pathname).slice(0, 100); } catch { return ''; }
}

function percentile(h: Uint32Array, total: number, q: number): number {
  if (total === 0) return 0;
  const want = q * total;
  let acc = 0;
  for (let i = 0; i < FRAME_BUCKETS; i++) {
    acc += h[i]!;
    if (acc >= want) {
      const mid = Math.sqrt(frameBucketFloorMs(i) * frameBucketFloorMs(i + 1));
      return Math.round(mid * 10) / 10;
    }
  }
  return Math.round(frameBucketFloorMs(FRAME_BUCKETS) * 10) / 10;
}

/** 4xciv.com and its subdomains share one choice through a cookie on the registrable domain. */
function sharedDomain(): string | null {
  const h = location.hostname;
  return h === SHARED_DOMAIN || h.endsWith(`.${SHARED_DOMAIN}`) ? SHARED_DOMAIN : null;
}

function readStored(): 'granted' | 'denied' | null {
  try {
    if (sharedDomain() !== null) {
      const m = /(?:^|;\s*)fxc_analytics_consent=(granted|denied)(?:;|$)/.exec(document.cookie);
      return m?.[1] === 'granted' || m?.[1] === 'denied' ? m[1] : null;
    }
    const v = localStorage.getItem(CONSENT_KEY);
    return v === 'granted' || v === 'denied' ? v : null;
  } catch {
    return null;
  }
}

function writeStored(v: 'granted' | 'denied'): void {
  try {
    const d = sharedDomain();
    if (d === null) {
      localStorage.setItem(CONSENT_KEY, v);
      return;
    }
    const scope = hostOnlyCookie() ? '' : `; Domain=.${d}`;
    document.cookie = `${CONSENT_COOKIE}=${v}${scope}; Path=/; Max-Age=${CONSENT_MAX_AGE_S}; SameSite=Lax; Secure`;
  } catch { /* private mode: this visit only */ }
}

function createGtagSink(id: string): AnalyticsSink {
  const w = window as unknown as Record<string, unknown> & { dataLayer?: unknown[] };
  let gtag: ((...args: unknown[]) => void) | null = null;
  function load(): (...args: unknown[]) => void {
    w[`ga-disable-${id}`] = false;
    if (gtag !== null) return gtag;
    const layer = (w.dataLayer ??= []);
    // gtag.js reads the `arguments` object itself off the data layer, not an array.
    gtag = function gtagShim() { layer.push(arguments); };
    w['gtag'] = gtag;
    gtag('js', new Date());
    gtag('config', id, {
      send_page_view: false, allow_google_signals: false, allow_ad_personalization_signals: false,
    });
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
    document.head.appendChild(s);
    return gtag;
  }
  return {
    send(events, { beacon }): void {
      const g = load();
      for (const e of events) g('event', e.name, beacon ? { ...e.params, transport_type: 'beacon' } : e.params);
    },
    stop(): void { w[`ga-disable-${id}`] = true; },
  };
}

type ViewName = 'solar_system' | 'cruise' | 'land' | 'walkaround' | 'hotkeys' | 'dvtable' | 'editor' | 'menu';
const VIEW_TITLES: Record<ViewName, string> = {
  solar_system: 'Solar System', cruise: 'Cruise', land: 'Land', walkaround: 'Walkaround',
  hotkeys: 'Hotkeys', dvtable: 'Delta-v table', editor: 'Editor', menu: 'Menu',
};
const VIEW_SECONDS: Partial<Record<ViewName, 's_solar' | 's_cruise' | 's_land' | 's_walk'>> = {
  solar_system: 's_solar', cruise: 's_cruise', land: 's_land', walkaround: 's_walk',
};

interface OpenView {
  readonly name: ViewName;
  readonly location: string;
  readonly openedMs: number;
  tileBytes: number;
  tiles: number;
  tileFails: number;
  renderScaleMin: number;
}

export function createAnalytics(config: AnalyticsConfig): AnalyticsHandle {
  if (typeof window === 'undefined') return OFF;
  const dev = devSinks();
  const measurementId = config.measurementId?.trim() ?? '';
  // Overwatch reports where GA4 does: a build carrying the production measurement id.
  const relay = dev === 'relay' || (dev === false && measurementId !== '') ? config.relay ?? null : null;
  if (dev === false) {
    // Automated browsers send nothing in any stage, so a headless check never reaches a dashboard,
    // and local dev reaches a sink only through the DEV recorder or log.
    if (navigator.webdriver || import.meta.env.DEV) return OFF;
    if (measurementId === '' && relay === null) return OFF;
  }
  const { site, renderer, scene } = config;
  const build = (config.build ?? '').slice(0, 12) || 'dev';

  const recorder = dev === 'record' || dev === 'relay'
    ? { calls: [] as RecordedCall[], ga: [] as SentEvent[], ow: [] as SentEvent[] }
    : null;
  const logSink = (s: Sink): AnalyticsSink => ({
    send: (events) => { for (const e of events) console.info(`[analytics:${s}]`, e.name, e.params); },
  });
  const recordedOw = (inner: AnalyticsSink | null, list: SentEvent[]): AnalyticsSink => ({
    send: (e, opts) => { list.push(...e); inner?.send(e, opts); },
    stop: () => inner?.stop?.(),
  });
  const sinks: Record<Sink, AnalyticsSink | null> = recorder
    ? { ga: { send: (e) => { recorder.ga.push(...e); } }, ow: recordedOw(relay?.sink ?? null, recorder.ow) }
    : dev === 'log'
      ? { ga: logSink('ga'), ow: logSink('ow') }
      : { ga: measurementId !== '' ? createGtagSink(measurementId) : null, ow: relay?.sink ?? null };

  // ---------- consent ----------
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
  const signal = nav.doNotTrack === '1' || nav.globalPrivacyControl === true;
  const stored = readStored();
  let consent: Consent = signal ? 'denied' : stored ?? 'pending';
  let region: Region | null = null;
  const bar = createConsentBar({ allow: () => choose('granted'), deny: () => choose('denied') });
  const row = createControlsRow(() => choose(consent === 'granted' ? 'denied' : 'granted'));
  const paintRow = (): void => row.paint({ on: consent === 'granted', signal });

  function choose(c: 'granted' | 'denied'): void {
    if (signal) return;
    consent = c;
    writeStored(c);
    bar.hide();
    paintRow();
    if (c === 'denied') {
      queue.length = 0;
      sinks.ga?.stop?.();
      sinks.ow?.stop?.();
    } else {
      flush(false);
    }
  }

  function applyRegion(r: Region): void {
    region = r;
    if (disposed || consent !== 'pending') return;
    if (r === 'row') {
      consent = 'granted';
      paintRow();
      flush(false);
    } else {
      bar.show();
    }
  }

  // ---------- queue ----------
  const queue: SentEvent[] = [];
  const sent: Record<Sink, number> = { ga: 0, ow: 0 };
  let exceptionsSent = 0;
  let perfSent = 0;
  const lastSeen = new Map<string, number>();
  let disposed = false;

  function push(name: EventName, params: Record<string, ParamValue>): void {
    const e: SentEvent = { name, params: { ...params, site, build }, atMs: performance.now() };
    queue.push(e);
    if (queue.length > QUEUE_MAX) queue.shift();
    if (relayEarly() && (ROUTES[name] as readonly Sink[]).includes('ow')) sinks.ow?.send([e], { beacon: false });
  }

  function flush(beacon: boolean): void {
    const bypass = consentBypass();
    if (!bypass && consent === 'pending') return;
    if (!bypass && consent === 'denied') { queue.length = 0; return; }
    const out: Record<Sink, SentEvent[]> = { ga: [], ow: [] };
    for (const e of queue.splice(0)) {
      if (e.name === 'exception') {
        if (exceptionsSent >= EXCEPTION_CAP) continue;
        exceptionsSent++;
      }
      if (e.name === 'perf_summary') {
        if (perfSent >= PERF_CAP) continue;
        perfSent++;
      }
      for (const s of ROUTES[e.name]) {
        if (sinks[s] === null || sent[s] >= CAPS[s]) continue;
        sent[s]++;
        out[s].push(e);
      }
    }
    for (const s of ['ga', 'ow'] as const) {
      if (out[s].length > 0) sinks[s]?.send(out[s], { beacon });
    }
  }

  // ---------- visit state ----------
  const focusAt = new Map<string, number>();
  const bodies = new Set<string>();
  const bandsSeen = new Set<string>();
  let deepest = -1;
  let errors = 0;
  let trips = 0;
  let walk: { body: string; atMs: number } | null = null;
  let landing: { body: string; atMs: number } | null = null;
  let warpHeld: { level: string; from_level: string } | null = null;
  let warpTimer: ReturnType<typeof setTimeout> | null = null;
  let menuTabId: string | null = null;
  let view: OpenView | null = null;
  let renderScaleNow = 1;
  const seconds = { s_solar: 0, s_cruise: 0, s_land: 0, s_walk: 0 };
  const visitHist = new Uint32Array(FRAME_BUCKETS);
  let visitEnded = false;

  function releaseWarp(): void {
    if (warpTimer !== null) clearTimeout(warpTimer);
    warpTimer = null;
    const held = warpHeld;
    warpHeld = null;
    if (held !== null && held.level !== held.from_level) push('warp_set', held);
  }

  const round1 = (v: number): number => Math.round(v * 10) / 10;

  function track<N extends EventName>(name: N, params: EventParams[N]): void {
    if (disposed || dropEvent() === name) return;
    const now = performance.now();
    recorder?.calls.push({ name, frame: profiler.frameCount, atMs: now });
    const p = { ...params } as Record<string, ParamValue>;
    const body = typeof p['body_id'] === 'string' ? p['body_id'] : '';
    let viewMoves = false;
    switch (name) {
      case 'warp_set':
        if (warpHeld === null) warpHeld = { level: String(p['level']), from_level: String(p['from_level']) };
        else warpHeld.level = String(p['level']);
        if (warpTimer !== null) clearTimeout(warpTimer);
        warpTimer = setTimeout(releaseWarp, WARP_SETTLE_MS);
        return;
      case 'atlas_focus':
        focusAt.set(body, now);
        bodies.add(body);
        break;
      case 'atlas_tier': {
        const band = String(p['band']);
        const key = `${body}:${band}`;
        if (bandsSeen.has(key)) return;
        bandsSeen.add(key);
        deepest = Math.max(deepest, BAND_ORDER.indexOf(band as BandName));
        break;
      }
      case 'surface_reached': {
        const f = focusAt.get(body);
        p['from'] = isMode('land') ? 'land' : 'atlas';
        p['s_since_focus'] = f === undefined ? -1 : Math.round((now - f) / 1000);
        break;
      }
      case 'walkaround_start':
        walk = { body, atMs: now };
        viewMoves = true;
        break;
      case 'walkaround_end':
        if (walk === null) return;
        p['seconds'] = round1((now - walk.atMs) / 1000);
        walk = null;
        viewMoves = true;
        break;
      case 'land_start':
        landing = { body, atMs: now };
        break;
      case 'launch':
        p['seconds'] = landing === null ? 0 : round1((now - landing.atMs) / 1000);
        landing = null;
        break;
      case 'exception':
        p['detail'] = scrubDetail(String(p['detail']));
        errors++;
        if (p['kind'] === 'tile' && view !== null) view.tileFails++;
        break;
      case 'trip_start':
        trips++;
        break;
      default:
        break;
    }
    // A trip's own two events are never deduped: two legs flown back to back on
    // one row carry the same figures, and dropping the second start would send
    // its end alone, while `visit_end.trips` counts every start.
    if (name !== 'trip_start' && name !== 'trip_end') {
      const key = name + JSON.stringify(p);
      const last = lastSeen.get(key);
      if (last !== undefined && now - last < DEDUPE_MS) return;
      lastSeen.set(key, now);
    }
    push(name, p);
    // Reaching SURFACE is a band like the others, for the band count and the deepest band.
    if (name === 'surface_reached') track('atlas_tier', { body_id: body, band: 'SURFACE', tier: 6 });
    if (viewMoves) refreshView();
  }

  // ---------- views ----------
  function currentView(): { name: ViewName; body: string | null; tab: string | null } {
    if (isMode('editor')) return { name: 'editor', body: null, tab: null };
    if (isMode('controls')) return { name: 'hotkeys', body: null, tab: null };
    if (isMode('dvtable')) return { name: 'dvtable', body: null, tab: null };
    const atlasUp = isMode('land') || (isMode('menu') && menuTabId === 'solar');
    if (walk !== null && atlasUp) return { name: 'walkaround', body: walk.body, tab: null };
    if (isMode('land')) return { name: 'land', body: landing?.body ?? null, tab: null };
    if (isMode('menu')) {
      return menuTabId === 'solar' || menuTabId === null
        ? { name: 'solar_system', body: null, tab: null }
        : { name: 'menu', body: null, tab: menuTabId };
    }
    return { name: 'cruise', body: null, tab: null };
  }

  function perfSummary(v: OpenView): void {
    const h = profiler.frameHistogram;
    let frames = 0;
    for (let i = 0; i < FRAME_BUCKETS; i++) frames += h[i]!;
    if (frames === 0) return;
    const mem = (performance as Performance & { memory?: { usedJSHeapSize: number } }).memory;
    push('perf_summary', {
      view: v.name,
      frames,
      p50_ms: percentile(h, frames, 0.5),
      p95_ms: percentile(h, frames, 0.95),
      p99_ms: percentile(h, frames, 0.99),
      long_frames: profiler.longFrames,
      render_scale_min: Math.round((scaleAtClose() ? renderScaleNow : v.renderScaleMin) * 100) / 100,
      draws: renderer?.info.render.calls ?? 0,
      textures: renderer?.info.memory.textures ?? 0,
      geometries: renderer?.info.memory.geometries ?? 0,
      ...(mem ? { heap_mb: Math.round(mem.usedJSHeapSize / 1048576) } : {}),
      tile_mb: round1(v.tileBytes / 1048576),
      tiles: v.tiles,
      tile_fails: v.tileFails,
    });
  }

  function closeView(final: boolean): void {
    const v = view;
    if (v === null) return;
    view = null;
    const heldMs = performance.now() - v.openedMs;
    const bucket = VIEW_SECONDS[v.name];
    if (bucket) seconds[bucket] += heldMs / 1000;
    if (heldMs >= PERF_VIEW_MIN_MS || final) perfSummary(v);
    const h = profiler.frameHistogram;
    for (let i = 0; i < FRAME_BUCKETS; i++) visitHist[i]! += h[i]!;
    profiler.resetFrameHistogram();
  }

  function refreshView(): void {
    if (disposed || !started) return;
    const v = currentView();
    const location = `/v/${v.tab !== null ? `${v.name}/${v.tab}` : v.body !== null ? `${v.name}/${v.body}` : v.name}`;
    if (view !== null && view.location === location) return;
    closeView(false);
    profiler.resetFrameHistogram();
    view = { name: v.name, location, openedMs: performance.now(), tileBytes: 0, tiles: 0, tileFails: 0, renderScaleMin: renderScaleNow };
    const title = v.tab !== null ? `${VIEW_TITLES.menu}: ${v.tab}` : VIEW_TITLES[v.name];
    track('page_view', { page_title: title, page_location: location });
    flush(false);
  }

  function entryView(): string {
    const v = currentView();
    if (v.name === 'solar_system' || v.name === 'cruise') return v.name;
    return v.name === 'land' || v.name === 'walkaround' ? 'atlas_body' : v.name;
  }

  function endVisit(): void {
    if (visitEnded) return;
    visitEnded = true;
    closeView(true);
    releaseWarp();
    let total = 0;
    for (let i = 0; i < FRAME_BUCKETS; i++) total += visitHist[i]!;
    push('visit_end', {
      s_solar: Math.round(seconds.s_solar), s_cruise: Math.round(seconds.s_cruise),
      s_land: Math.round(seconds.s_land), s_walk: Math.round(seconds.s_walk),
      deepest_band: deepest >= 0 ? BAND_ORDER[deepest]! : 'none',
      bodies: bodies.size, trips, errors, p95_ms: percentile(visitHist, total, 0.95),
    });
  }

  // ---------- listeners ----------
  let started = false;
  let hiddenTimer: ReturnType<typeof setTimeout> | null = null;
  const onPageHide = (): void => {
    endVisit();
    flush(true);
  };
  const onVisibility = (): void => {
    if (document.visibilityState === 'hidden') {
      releaseWarp();
      flush(true);
      hiddenTimer ??= setTimeout(() => { hiddenTimer = null; endVisit(); flush(true); }, HIDDEN_END_MS);
    } else if (hiddenTimer !== null) {
      clearTimeout(hiddenTimer);
      hiddenTimer = null;
    }
  };
  const onError = (e: ErrorEvent): void => {
    track('exception', { kind: 'error', detail: e.message || 'error', fatal: false });
  };
  const onRejection = (e: PromiseRejectionEvent): void => {
    const r: unknown = e.reason;
    track('exception', { kind: 'rejection', detail: r instanceof Error ? r.message : String(r), fatal: false });
  };
  window.addEventListener('pagehide', onPageHide);
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  const offMode = onModeChange(() => refreshView());
  const timer = setInterval(() => flush(false), FLUSH_MS);

  let observer: PerformanceObserver | null = null;
  try {
    observer = new PerformanceObserver((list) => {
      if (view === null) return;
      for (const e of list.getEntries()) {
        if (!e.name.includes('/dtm/')) continue;
        view.tiles++;
        view.tileBytes += (e as PerformanceResourceTiming).transferSize;
      }
    });
    observer.observe({ type: 'resource' });
  } catch {
    observer = null;
  }

  const offFirstFrame = profiler.onFirstFrame((ms) => {
    track('app_ready', {
      entry_view: entryView(),
      load_ms: Math.round(ms),
      ...(renderer ? { webgl: renderer.capabilities.isWebGL2 ? 2 as const : 1 as const } : {}),
      input: window.matchMedia?.('(pointer: coarse)').matches ? 'touch' : 'mouse',
      screen: window.innerWidth < 768 ? 's' : window.innerWidth < 1280 ? 'm' : 'l',
    });
  });

  // After the boot's own synchronous mode writes, so the first page view names
  // the view the visitor lands on rather than the one the boot passed through.
  const startTimer = setTimeout(() => {
    started = true;
    const utm = (k: string): string | undefined => urlParam(k)?.slice(0, 60) || undefined;
    const source = utm('utm_source');
    const medium = utm('utm_medium');
    const campaign = utm('utm_campaign');
    track('visit', {
      referrer: referrerOf(document.referrer), entry_view: entryView(),
      ...(source ? { utm_source: source } : {}),
      ...(medium ? { utm_medium: medium } : {}),
      ...(campaign ? { utm_campaign: campaign } : {}),
    });
    refreshView();
  }, 0);

  paintRow();
  if (consent === 'pending') {
    const forced = regionOverride();
    if (forced !== false) applyRegion(forced);
    else if (relay === null) applyRegion('eu');
    else {
      const wait = new Promise<null>((r) => setTimeout(() => r(null), REGION_WAIT_MS));
      void Promise.race([relay.region().catch(() => null), wait]).then((r) => applyRegion(r ?? 'eu'));
    }
  }

  const handle: AnalyticsHandle = {
    track,
    menuTab(id: string): void {
      menuTabId = id;
      refreshView();
    },
    renderScale(scale: number): void {
      renderScaleNow = scale;
      if (view !== null) view.renderScaleMin = Math.min(view.renderScaleMin, scale);
    },
    dispose(): void {
      if (disposed) return;
      releaseWarp();
      flush(true);
      disposed = true;
      clearTimeout(startTimer);
      clearInterval(timer);
      if (hiddenTimer !== null) clearTimeout(hiddenTimer);
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
      offMode();
      offFirstFrame();
      observer?.disconnect();
      bar.hide();
      row.dispose();
      if (scene) delete scene.userData['simAnalytics'];
      clearAnalytics(handle);
    },
  };

  if (recorder && scene) {
    scene.userData['simAnalytics'] = {
      calls: recorder.calls,
      ga: recorder.ga,
      ow: recorder.ow,
      state: () => ({
        consent, region, signal, bar: bar.shown, queued: queue.length,
        view: view?.location ?? null, frame: profiler.frameCount,
      }),
      flush: () => flush(false),
    };
  }
  return handle;
}
